import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pagination } from "@mui/material";
import { DarkMode, LightMode, Visibility } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import {
  Edit,
  BookOpen,
  Users,
  Calendar,
  Star,
  TrendingUp,
  Plus,
  Trash2,
} from "lucide-react";

export default function QuizSubjects() {
  const [currentPage, setCurrentPage] = useState(1);
  const [subjectsData, setSubjectsData] = useState([]);
  /* Dark Mode is now hardcoded */

  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    category: "",
    title: "",
    description: "",
  });

  const [modalType, setModalType] = useState("add"); // "add" or "edit"
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const itemsPerPage = 6;

  // Theme classes - Hardcoded Dark Mode
  const bgGradient = "bg-gradient-to-br from-gray-900 to-gray-800";
  const cardBg = "bg-gray-800";
  const cardBorder = "border-gray-700";
  const textPrimary = "text-white";
  const textSecondary = "text-gray-300";
  const textMuted = "text-gray-400";
  const headerBorder = "border-gray-700";
  const footerBg = "bg-gray-900/50";

  const fetchSubjectsList = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${server}/quizSubject/all`, {
        params: {
          pageNo: 1,
          pageSize: 100,
        },
      });

      setSubjectsData(data?.data || []);
    } catch (e) {
      setSubjectsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjectsList();
  }, []);

  // Calculate pagination
  const totalPages = Math.ceil(subjectsData?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentSubjects = subjectsData?.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className={`${cardBg} rounded-2xl shadow-lg border ${cardBorder} p-6 animate-pulse`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-400 rounded-lg"></div>
              <div className="h-6 bg-gray-400 rounded w-32"></div>
            </div>
            <div className="h-6 bg-gray-400 rounded w-16"></div>
          </div>
          <div className="h-4 bg-gray-400 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-400 rounded w-3/4 mb-4"></div>
        </div>
      ))}
    </div>
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenModal = (type, subject = null) => {
    setModalType(type);
    if (type === "edit" && subject) {
      setSelectedSubjectId(subject.id);
      setFormData({
        category: subject.category,
        title: subject.title || subject.subject,
        description: subject.description,
      });
    } else {
      setSelectedSubjectId(null);
      setFormData({
        category: "",
        title: "",
        description: "",
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (modalType === "add") {
        await axios.post(`${server}/quizSubject/new`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
        toast.success("Subject added successfully");
      } else if (modalType === "edit") {
        await axios.put(
          `${server}/quizSubject/updateById?id=${selectedSubjectId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success("Subject updated successfully");
      }

      setIsModalOpen(false);
      fetchSubjectsList();
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSubject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subject?"))
      return;
    try {
      setIsLoading(true);
      await axios.delete(`${server}/quizSubject/deleteById?id=${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success("Subject deleted successfully");
      fetchSubjectsList();
    } catch (error) {
      setIsLoading(false);
      toast.error(error.response?.data?.message || "Delete failed");
    }
  };

  return (
    <div
      className={`min-h-screen ${bgGradient} py-8 px-4 transition-colors duration-300 relative`}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 relative"
        >
          {/* REMOVED THEME TOGGLE BUTTON */}

          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-4xl font-bold ${textPrimary}`}>
              Quiz Subjects
            </h1>
          </div>
          <p className={`text-lg ${textSecondary} max-w-2xl mx-auto mb-6`}>
            Manage your various quiz subjects and categories here.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleOpenModal("add")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold"
          >
            <Plus className="w-5 h-5" />
            Add Subject
          </motion.button>
        </motion.div>

        {/* Subjects Grid */}
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
            >
              <AnimatePresence>
                {currentSubjects?.map((subject) => (
                  <motion.div
                    key={subject.id}
                    variants={cardVariants}
                    whileHover={{ y: -5, scale: 1.02 }}
                    className={`${cardBg} rounded-2xl shadow-lg overflow-hidden border ${cardBorder} transition-all duration-300 group cursor-pointer`}
                  >
                    {/* Card Header */}
                    <div
                      className={`p-6 border-b ${headerBorder} bg-gradient-to-r from-gray-700 to-gray-800`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                            <span className="text-2xl">
                              {subject.icon || "📚"}
                            </span>
                          </div>
                          <div>
                            <h3
                              className={`text-xl font-bold ${textPrimary} group-hover:text-blue-500 transition-colors duration-200`}
                            >
                              {subject.title || subject.subject}
                            </h3>
                            <p className={`text-sm ${textMuted} mt-1`}>
                              ID: {subject.id}
                            </p>
                          </div>
                        </div>
                      </div>

                      <p
                        className={`text-sm ${textSecondary} line-clamp-2 mb-3`}
                      >
                        {subject.description || "No description available"}
                      </p>

                      <div
                        className={`flex items-center text-sm ${textMuted} gap-4`}
                      >
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {subject.createdAt
                            ? new Date(subject.createdAt).toLocaleDateString()
                            : "N/A"}
                        </span>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div
                      className={`px-6 py-4 ${footerBg} border-t ${headerBorder}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${textMuted}`}>
                          {/* Created by: {subject.createdBy || 'Admin'} */}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleOpenModal("edit", subject)}
                            className={`p-2 text-gray-400 hover:text-green-400 hover:bg-green-900/50 rounded-lg transition-all duration-300 flex gap-1 items-center`}
                            title="Edit Subject"
                          >
                            <Edit className="w-4 h-4" />
                            <span className="text-xs">Edit</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSubject(subject.id);
                            }}
                            className={`p-2 text-gray-400 hover:text-red-400 hover:bg-red-900/50 rounded-lg transition-all duration-300 flex gap-1 items-center`}
                            title="Delete Subject"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-xs">Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex justify-center bg-gray-200 rounded-full px-4 py-1 border border-gray-800 w-fit mx-auto"
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </motion.div>
            )}
          </>
        )}

        {/* Empty State */}
        {!isLoading && subjectsData?.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="text-8xl mb-6">📚</div>
            <h3 className={`text-3xl font-bold ${textPrimary} mb-4`}>
              No Quizzes Found
            </h3>
            <p className={`text-lg ${textSecondary} max-w-md mx-auto mb-6`}>
              It looks like there are no quiz subjects in the system yet.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchSubjectsList}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Refresh
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Add Subject Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center pt-10 p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`${cardBg} w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border ${cardBorder}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h2 className={`text-2xl font-bold ${textPrimary} mb-6`}>
                  {modalType === "add" ? "Add New Subject" : "Edit Subject"}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium ${textSecondary} mb-1`}
                    >
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 rounded-lg border bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      placeholder="e.g. Mathematics"
                      required
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium ${textSecondary} mb-1`}
                    >
                      Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 rounded-lg border bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      required
                    >
                      <option value="" disabled>
                        Select Category
                      </option>
                      <option value="General Knowledge">
                        General Knowledge
                      </option>
                      <option value="General Science">General Science</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium ${textSecondary} mb-1`}
                    >
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-2 rounded-lg border bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                      placeholder="Brief description of the subject..."
                      required
                    />
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      disabled={isSubmitting}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                        isSubmitting
                          ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium transition-all shadow-md ${
                        isSubmitting
                          ? "opacity-70 cursor-not-allowed"
                          : "hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg"
                      }`}
                    >
                      {isSubmitting
                        ? "Processing..."
                        : modalType === "add"
                        ? "Create Subject"
                        : "Update Subject"}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
